/**
 * Destinations a home tile may point at (HOME-04/05). Organizers choose which tiles appear,
 * their order and their labels; the attendee app maps these keys to routes.
 */
export const HOME_TILE_INTERNAL_KEYS = [
  'agenda',
  'speakers',
  'directory',
  'matches',
  'schedule',
  'announcements',
  'venue',
  'profile',
  'info',
] as const;

export type HomeTileInternalKey = (typeof HOME_TILE_INTERNAL_KEYS)[number];

export function isHomeTileInternalKey(value: string): value is HomeTileInternalKey {
  return (HOME_TILE_INTERNAL_KEYS as readonly string[]).includes(value);
}

/**
 * The glyphs an organizer may put on a link of her own (HOME-04). A subset of the brand kit's
 * set: the chevrons are furniture, `settings` is the console's own mark, and the external arrow
 * is drawn on every one of these rows already (HOME-05), so offering it would offer the same
 * thing twice.
 *
 * Every key here is also an icon name in `@gftg/ui`. The console's picker renders them through
 * `Icon`, which takes an `IconName`, so a key that drifts out of the kit fails the build rather
 * than falling back to a calendar on the home screen.
 */
export const HOME_TILE_ICON_KEYS = [
  'agenda',
  'star',
  'people',
  'matches',
  'speakers',
  'bell',
  'map',
  'golf',
  'heart',
  'bag',
  'share',
  'search',
  'check',
] as const;

export type HomeTileIconKey = (typeof HOME_TILE_ICON_KEYS)[number];

export function isHomeTileIconKey(value: string): value is HomeTileIconKey {
  return (HOME_TILE_ICON_KEYS as readonly string[]).includes(value);
}

/**
 * The client's own destinations, kept in `events.links` (HOME-05, footer). They are one JSONB
 * column rather than columns or tile rows because the footer needs them addressable by name,
 * while a tile is a positioned, renamable entry the organizer curates.
 */
export const EVENT_LINK_KEYS = [
  'website',
  'privacy',
  'donate',
  'mentorshipDonation',
  'golf',
  'silentAuction',
  'store',
  'mediaKit',
] as const;

export type EventLinkKey = (typeof EVENT_LINK_KEYS)[number];
export type EventLinks = Partial<Record<EventLinkKey, string>>;
